"use client";
import { ArrowRight, Linkedin } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";

type TeamMember = {
  name: string;
  role: string;
  description: string;
  image: string;
  linkedinUrl?: string;
};

type TeamCardProps = {
  member: TeamMember;
};

export function TeamCard({ member }: TeamCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);

  const handleMobileFlip = () => {
    setIsFlipped(!isFlipped);
  };

  // Reset flip state only when crossing mobile/desktop breakpoint
  useEffect(() => {
    // Tailwind's md breakpoint is 768px
    const mediaQuery = window.matchMedia("(min-width: 768px)");

    const handleBreakpointChange = () => {
      // Reset flip state when transitioning between mobile and desktop
      setIsFlipped(false);
    };

    // Add listener for breakpoint changes
    mediaQuery.addEventListener("change", handleBreakpointChange);

    return () => {
      mediaQuery.removeEventListener("change", handleBreakpointChange);
    };
  }, []);

  return (
    <div
      // biome-ignore lint/a11y/noNoninteractiveTabindex: <we need to be able to focus the card>
      tabIndex={0}
      className="group focus:outline-none perspective-1000 h-102 lg:h-[26rem] w-full"
    >
      <div
        className={`team-card-inner relative w-full h-full transition-transform duration-700  md:group-hover:rotate-y-180 md:group-focus:rotate-y-180 ${isFlipped ? "rotate-y-180" : ""}`}
        style={{
          transformStyle: "preserve-3d",
        }}
      >
        {/* Front of card - Image */}
        <div className="absolute inset-0 w-full h-full backface-hidden rounded-xl overflow-hidden shadow-lg">
          <Image
            src={member.image}
            alt={member.name}
            width={400}
            height={400}
            quality={85}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t via-black/30 from-black/50 to-transparent" />
          <div className="absolute bottom-4 left-4 text-white">
            <h4 className="text-lg font-semibold">{member.name}</h4>
            <p className="text-sm font-medium">{member.role}</p>
          </div>

          {/* Mobile flip arrow - only visible on mobile */}
          <button
            type="button"
            onClick={handleMobileFlip}
            className="absolute bottom-4 right-4 md:hidden bg-black/50 backdrop-blur-sm rounded-full p-2 text-white hover:bg-black/70 transition-colors duration-200"
            aria-label={`Flip card to see more about ${member.name}`}
          >
            <ArrowRight
              size={20}
              className={`transition-transform duration-300 ${isFlipped ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        {/* Back of card - Information */}
        <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 rounded-xl bg-[#edeadd] text-black p-4 flex flex-col justify-between shadow-lg">
          {/* Mobile flip arrow for back side - only visible on mobile */}
          <button
            type="button"
            onClick={handleMobileFlip}
            className="absolute bottom-4 right-4 md:hidden bg-black/10 backdrop-blur-sm rounded-full p-2 text-black hover:bg-black/20 transition-colors duration-200"
            aria-label={`Flip card back to see ${member.name}'s photo`}
          >
            <ArrowRight
              size={20}
              className={`transition-transform duration-300 ${isFlipped ? "rotate-180" : ""}`}
            />
          </button>
          <div>
            <div className="flex justify-between items-center">
              <div>
                <h4 className="text-xl font-bold">{member.name}</h4>
                <p className="text-brand-red font-semibold text-sm uppercase tracking-wide">
                  {member.role}
                </p>
              </div>
              {member.linkedinUrl && (
                <a
                  href={member.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 transition-colors duration-200"
                  title={`Connect with ${member.name} on LinkedIn`}
                >
                  <Linkedin size={36} className="fill-brand-blue stroke-0" />
                </a>
              )}
            </div>
            <div className="w-12 h-px bg-black/30 my-2" />
          </div>

          <div className="flex-grow flex items-start">
            <p className="text-sm opacity-90" style={{ lineHeight: 1.5 }}>
              {member.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
